import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Check, X, Edit3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatCurrency, parseBrazilianCurrency, formatBrazilianNumber } from "@/lib/utils";

interface InlineCostInputProps {
  workOrderId: string;
  currentCost: number | null | undefined;
  onUpdate: (id: string, cost: number | null) => void;
  disabled?: boolean;
}

export function InlineCostInput({ workOrderId, currentCost, onUpdate, disabled = false }: InlineCostInputProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(currentCost?.toString() || "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    const numValue = parseBrazilianCurrency(value);
    
    if (numValue !== null && (isNaN(numValue) || numValue < 0)) {
      setValue(currentCost ? formatBrazilianNumber(currentCost) : "");
      setIsEditing(false);
      return;
    }

    if (numValue !== currentCost) {
      onUpdate(workOrderId, numValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setValue(currentCost ? formatBrazilianNumber(currentCost) : "");
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  if (disabled) {
    return <span>{currentCost ? formatCurrency(currentCost) : "N/A"}</span>;
  }

  if (!isEditing) {
    return (
      <div 
        className="flex items-center gap-2 cursor-pointer hover:bg-accent/50 px-2 py-1 rounded-md transition-colors group"
        onClick={() => {
          setIsEditing(true);
          setValue(currentCost ? formatBrazilianNumber(currentCost) : "");
        }}
      >
        <span>{currentCost ? formatCurrency(currentCost) : formatCurrency(0)}</span>
        <Edit3 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <span className="text-xs text-muted-foreground mr-1">R$</span>
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => {
          const inputValue = e.target.value;
          // Permite apenas números, vírgula e ponto
          if (/^[\d.,]*$/.test(inputValue)) {
            setValue(inputValue);
          }
        }}
        onKeyDown={handleKeyDown}
        onBlur={handleSave}
        className="h-7 w-28 text-sm"
        placeholder="0,00"
      />
      <Button
        size="sm"
        variant="ghost"
        className="h-6 w-6 p-0"
        onClick={handleSave}
      >
        <Check className="h-3 w-3 text-green-600" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="h-6 w-6 p-0"
        onClick={handleCancel}
      >
        <X className="h-3 w-3 text-red-600" />
      </Button>
    </div>
  );
}
